import { authenticatedFetch } from "@lumi/data";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { createRefreshCallback } from "../../auth/refreshCallback";

/**
 * Response from watchlist status endpoint
 */
interface WatchlistStatusResponse {
  inWatchlist: boolean;
}

/**
 * Response from add/remove watchlist endpoint
 */
interface WatchlistMutationResponse {
  inWatchlist: boolean;
}

/**
 * Hook to manage watchlist state with optimistic updates
 *
 * @param titleId - The ID of the title to manage watchlist for
 * @returns Object with watchlist state and mutation functions
 *
 * @example
 * const { inWatchlist, add, remove, toggle, loading, error } = useWatchlist('title-123');
 *
 * // Add to watchlist with optimistic update
 * await add();
 *
 * // Remove from watchlist
 * await remove();
 *
 * // Toggle watchlist status
 * await toggle();
 */
export function useWatchlist(titleId: string) {
  const { accessToken, refreshAuth } = useAuth();
  const [inWatchlist, setInWatchlist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Fetch initial watchlist status
  const fetchWatchlistStatus = useCallback(async () => {
    if (!accessToken || !titleId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE}/v1/watchlist/status/${titleId}`;
      const response = await authenticatedFetch<WatchlistStatusResponse>(
        url,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setInWatchlist(response.inWatchlist);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch watchlist status");
    } finally {
      setLoading(false);
    }
  }, [accessToken, titleId, API_BASE, refreshAuth]);

  // Add to watchlist with optimistic update
  const add = useCallback(async () => {
    if (!accessToken || mutating || inWatchlist) return;

    // Optimistic update
    const previousState = inWatchlist;
    setInWatchlist(true);
    setMutating(true);
    setError(null);

    try {
      const url = `${API_BASE}/v1/watchlist/${titleId}`;
      await authenticatedFetch<WatchlistMutationResponse>(
        url,
        { method: "POST" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );
    } catch (err) {
      // Rollback on error
      setInWatchlist(previousState);
      setError(err instanceof Error ? err.message : "Failed to add to watchlist");
    } finally {
      setMutating(false);
    }
  }, [accessToken, inWatchlist, mutating, titleId, API_BASE, refreshAuth]);

  // Remove from watchlist with optimistic update
  const remove = useCallback(async () => {
    if (!accessToken || mutating || !inWatchlist) return;

    // Optimistic update
    const previousState = inWatchlist;
    setInWatchlist(false);
    setMutating(true);
    setError(null);

    try {
      const url = `${API_BASE}/v1/watchlist/${titleId}`;
      await authenticatedFetch<WatchlistMutationResponse>(
        url,
        { method: "DELETE" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );
    } catch (err) {
      // Rollback on error
      setInWatchlist(previousState);
      setError(err instanceof Error ? err.message : "Failed to remove from watchlist");
    } finally {
      setMutating(false);
    }
  }, [accessToken, inWatchlist, mutating, titleId, API_BASE, refreshAuth]);

  // Toggle watchlist with optimistic update
  const toggle = useCallback(async () => {
    if (inWatchlist) {
      await remove();
    } else {
      await add();
    }
  }, [inWatchlist, add, remove]);

  // Load initial status
  useEffect(() => {
    void fetchWatchlistStatus();
  }, [fetchWatchlistStatus]);

  return {
    inWatchlist,
    add,
    remove,
    toggle,
    loading,
    mutating,
    error,
    refetch: fetchWatchlistStatus,
  };
}
